import { useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";
import type { Plugin } from "chart.js";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { getCSSVar } from "@/lib/chart";
import type { FilterState, DrillLevel } from "@/types/filters";
import type { SchedulingRow } from "@/types/scheduling";
import {
  useCancellationData,
  type CancellationDrillRow,
  type CancelReasonKey,
} from "@/hooks/useCancellationData";
import { useRollupCancellation } from "@/hooks/useRollupCancellation";
import { comparePaths } from "@/lib/rollupCompare";
import type { DataPath } from "@/lib/rollupFlag";
import type { RollupPayload } from "@/hooks/useRollupData";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Card } from "@/components/ui/card";

const DRILL_LEVELS: { key: DrillLevel; label: string }[] = [
  { key: "country", label: "Country" },
  { key: "group", label: "Group" },
  { key: "division", label: "Division" },
  { key: "site", label: "Site" },
  { key: "dvm", label: "DVM" },
];

const TOGGLE_ITEM_CLS =
  "h-auto text-[12px] px-3 py-1 border-black/15 bg-white text-gray-600 font-normal hover:bg-gray-50 hover:text-gray-600 transition-colors data-[state=on]:bg-[var(--brand-dark-teal)] data-[state=on]:text-white data-[state=on]:border-[var(--brand-dark-teal)]";

// Each reason maps to a different management action — that's the whole point
// of breaking cancellations out by reason rather than reporting one number.
// Colors reference CSS var names so both DOM swatches and the canvas pie can
// use them (the canvas can't resolve var() — see reasonColor).
const REASON_META: Record<
  CancelReasonKey,
  { label: string; varName: string; alpha?: number; hint: string }
> = {
  no_show: {
    label: "No-show",
    varName: "--perf-red",
    hint: "Reminder cadence, deposit policy",
  },
  pending_reschedule: {
    label: "Pending reschedule",
    varName: "--brand-orange-mid",
    hint: "Follow-up call list — recoverable visits",
  },
  pet_seen_elsewhere: {
    label: "Seen elsewhere",
    varName: "--brand-lilac-mid",
    hint: "Access problem — couldn't get in soon enough",
  },
  concern_resolved: {
    label: "Concern resolved",
    varName: "--perf-green",
    hint: "Benign — no action needed",
  },
  other: {
    label: "Other",
    varName: "--perf-gray",
    hint: "Review a sample for patterns",
  },
  unknown_reason: {
    label: "Unknown",
    varName: "--perf-gray",
    alpha: 0.55,
    hint: "Front-desk logging gap — coach reason capture",
  },
};

// Resolved color for canvas use; alpha appended as 2-digit hex.
function reasonColor(key: CancelReasonKey): string {
  const meta = REASON_META[key];
  const hex = getCSSVar(meta.varName);
  if (meta.alpha === undefined) return hex;
  return `${hex}${Math.round(meta.alpha * 255)
    .toString(16)
    .padStart(2, "0")}`;
}

function SwatchStyle(key: CancelReasonKey): React.CSSProperties {
  const meta = REASON_META[key];
  return { background: `var(${meta.varName})`, opacity: meta.alpha };
}

// Draws each slice's share % at its midpoint. Slices under 6% are skipped —
// the label text no longer fits the wedge and starts overlapping neighbors.
const MIN_LABEL_SHARE = 0.06;
interface ArcProps {
  x: number;
  y: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
}

const pieSlicePercentPlugin: Plugin<"pie"> = {
  id: "pieSlicePercent",
  afterDatasetDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const dataset = chart.data.datasets[0];
    if (!dataset) return;
    const total = (dataset.data as number[]).reduce((s, v) => s + v, 0);
    if (total <= 0) return;

    ctx.save();
    ctx.font = "600 11px sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    meta.data.forEach((arc, i) => {
      const value = (dataset.data as number[])[i];
      if (value === undefined) return;
      const share = value / total;
      if (share < MIN_LABEL_SHARE) return;
      // Named rather than Record<string, number>: indexing a Record yields
      // `number | undefined` under noUncheckedIndexedAccess, so every one of these
      // six reads looked nullable when the arc always supplies all six.
      const props = (
        arc as unknown as { getProps: (p: string[], final: boolean) => ArcProps }
      ).getProps(["x", "y", "innerRadius", "outerRadius", "startAngle", "endAngle"], true);
      const angle = (props.startAngle + props.endAngle) / 2;
      const radius = (props.innerRadius + props.outerRadius) / 2;
      const x = props.x + Math.cos(angle) * radius;
      const y = props.y + Math.sin(angle) * radius;
      ctx.fillText(`${Math.round(share * 100)}%`, x, y);
    });
    ctx.restore();
  },
};

function fmtPct(n: number, decimals = 1): string {
  return `${(n * 100).toFixed(decimals)}%`;
}

// For cancellations, an increase is bad — arrows are red when rising.
function RateDeltaVsPY({ cur, py }: { cur: number; py: number | null }) {
  if (py === null) return <span className="text-gray-300">—</span>;
  const deltaPts = (cur - py) * 100;
  const up = deltaPts >= 0;
  const color = up ? "var(--perf-red)" : "var(--perf-green)";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center gap-0.5 font-medium tabular-nums" style={{ color }}>
      <Icon className="h-3.5 w-3.5" />
      {up ? "+" : ""}
      {deltaPts.toFixed(1)} pts
    </span>
  );
}

interface KPICardProps {
  label: string;
  value: string;
  sub: React.ReactNode;
}

function KPICard({ label, value, sub }: KPICardProps) {
  return (
    <Card className="flex-1 bg-white rounded-lg p-4 gap-0 shadow-none border border-black/10">
      <div className="text-[12px] text-gray-500 mb-1">{label}</div>
      <div className="text-[28px] font-semibold tracking-tight leading-none">{value}</div>
      <div className="h-px bg-gray-200 my-2" />
      <div className="text-[11px] text-gray-500">{sub}</div>
    </Card>
  );
}

type SortKey = "booked" | "canceled" | "cancelRate" | "noShows" | "noShowRate" | "vsPy";
type SortDir = "asc" | "desc";

function sortValue(row: CancellationDrillRow, key: SortKey): number {
  switch (key) {
    case "booked":
      return row.booked;
    case "canceled":
      return row.canceled;
    case "cancelRate":
      return row.cancelRate;
    case "noShows":
      return row.noShows;
    case "noShowRate":
      return row.noShowRate;
    case "vsPy":
      return row.pyCancelRate === null ? -Infinity : row.cancelRate - row.pyCancelRate;
  }
}

function SortableHead({
  label,
  sortKey,
  sort,
  onToggle,
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDir };
  onToggle: (key: SortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => onToggle(sortKey)}
      className={[
        "px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.5px] cursor-pointer select-none transition-colors whitespace-nowrap",
        active ? "text-gray-600" : "text-gray-400 hover:text-gray-600",
      ].join(" ")}
    >
      {label}
      {active && (
        <span className="ml-0.5" style={{ color: "var(--brand-dark-teal)" }}>
          {sort.dir === "asc" ? "↑" : "↓"}
        </span>
      )}
    </th>
  );
}

interface CancellationTabProps {
  rows: SchedulingRow[];
  filters: FilterState;
  startDate: string;
  endDate: string;
  // Lifted, because it decides whether the rollup request must ask for wide
  // doctor rows -- per-doctor measures live only there.
  drillLevel: DrillLevel;
  setDrillLevel: (l: DrillLevel) => void;
  rollupPayload: RollupPayload | null;
  dataPath: DataPath;
}

export function CancellationTab({
  rows,
  filters,
  startDate,
  endDate,
  drillLevel,
  setDrillLevel,
  rollupPayload,
  dataPath,
}: CancellationTabProps) {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "cancelRate",
    dir: "desc",
  });

  const rawCancel = useCancellationData(rows, filters, startDate, endDate, drillLevel);
  // The tab keeps its own drill level, so the rollup hook is given THAT one --
  // and AppPage told the fetch about it, which is what puts wide doctor rows in
  // the payload. Without them every per-doctor row here would read zero.
  const rollupCancel = useRollupCancellation(
    rollupPayload,
    { ...filters, drillLevel },
    filters.capGranularity
  );
  useEffect(() => {
    if (dataPath !== "compare" || !rollupCancel) return;
    const tag = `${filters.period}/${filters.capGranularity}/cancel:${drillLevel}`;
    comparePaths(`${tag} cancel.kpis`, rawCancel.kpis, rollupCancel.kpis);
    comparePaths(`${tag} cancel.reasonMix`, rawCancel.reasonMix, rollupCancel.reasonMix);
    const byId = new Map(rollupCancel.drillRows.map((r) => [r.id, r]));
    comparePaths(
      `${tag} cancel.drill`,
      rawCancel.drillRows,
      rawCancel.drillRows.map((r) => byId.get(r.id) ?? null)
    );
  }, [dataPath, rawCancel, rollupCancel, filters.period, filters.capGranularity, drillLevel]);

  const { kpis, reasonMix, drillRows } =
    dataPath === "rollup" && rollupCancel ? rollupCancel : rawCancel;

  // Rank fixed to cancel rate (worst first), independent of the active sort
  const rankById = new Map(drillRows.map((r, i) => [r.id, i + 1]));

  const sorted = [...drillRows].sort((a, b) => {
    const cmp = sortValue(a, sort.key) - sortValue(b, sort.key);
    return sort.dir === "asc" ? cmp : -cmp;
  });

  function toggleSort(key: SortKey) {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }
    );
  }

  // Auto-insight: worst no-show rate among entities with enough volume to matter
  const MIN_BOOKED = 20;
  const worstNoShow = drillRows
    .filter((r) => r.booked >= MIN_BOOKED && r.noShowRate > 0)
    .sort((a, b) => b.noShowRate - a.noShowRate)[0];

  const recoverable = reasonMix.find((m) => m.key === "pending_reschedule")?.count ?? 0;

  const entityLabel = DRILL_LEVELS.find((l) => l.key === drillLevel)?.label ?? "";
  const activeReasons = reasonMix.filter((m) => m.count > 0);

  return (
    <div className="flex flex-col gap-8">
      {/* Work in progress banner */}
      <div className="flex items-center gap-2 rounded-lg border border-[var(--brand-orange-light)] bg-[var(--brand-orange-light)]/10 px-4 py-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.6px] text-[var(--brand-orange-dark)]">
          Work in Progress
        </span>
        <span className="text-[12px] text-gray-500">
          This tab is experimental — metrics and layout may change.
        </span>
      </div>

      {/* KPI cards */}
      <div className="flex items-stretch gap-3">
        <KPICard
          label="Cancellation rate"
          value={fmtPct(kpis.cancelRate)}
          sub={
            <span className="flex justify-between">
              <span>of booked visits (est.)</span>
              <RateDeltaVsPY cur={kpis.cancelRate} py={kpis.pyCancelRate} />
            </span>
          }
        />
        <KPICard
          label="No-show rate"
          value={fmtPct(kpis.noShowRate)}
          sub={
            <span className="flex justify-between">
              <span>of booked visits (est.)</span>
              <RateDeltaVsPY cur={kpis.noShowRate} py={kpis.pyNoShowRate} />
            </span>
          }
        />
        <KPICard
          label="Canceled appointments"
          value={kpis.totalCanceled.toLocaleString()}
          sub={
            kpis.pyTotalCanceled !== null
              ? `vs. PY ${kpis.pyTotalCanceled.toLocaleString()}`
              : "for selected scope & period"
          }
        />
        <KPICard
          label="Avg cancellation lead time"
          value={kpis.avgCancelLeadDays !== null ? `${kpis.avgCancelLeadDays.toFixed(1)} d` : "—"}
          sub={
            kpis.pyAvgCancelLeadDays !== null
              ? `vs. PY ${kpis.pyAvgCancelLeadDays.toFixed(1)} d`
              : "days before the appointment"
          }
        />
      </div>

      {/* Reason breakdown (pie, left) + auto-insights (right) */}
      <div>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-500 mb-1">
          Why appointments were canceled
        </h2>
        <p className="text-[12px] text-gray-400 mb-3">
          Each reason calls for a different response — no-shows and pending reschedules are the
          actionable ones.
        </p>

        <div className="grid lg:grid-cols-2 gap-3 items-stretch">
          <Card className="bg-white rounded-lg p-4 gap-0 shadow-none border border-black/10">
            {kpis.totalCanceled === 0 ? (
              <div className="py-6 text-center text-[13px] text-gray-400">
                No cancellations for the selected scope and period.
              </div>
            ) : (
              <div className="flex items-center gap-5 flex-wrap">
                <div className="w-[180px] h-[180px] shrink-0 mx-auto">
                  <Pie
                    data={{
                      labels: activeReasons.map((m) => REASON_META[m.key].label),
                      datasets: [
                        {
                          data: activeReasons.map((m) => m.count),
                          backgroundColor: activeReasons.map((m) => reasonColor(m.key)),
                          borderColor: "#ffffff",
                          borderWidth: 2,
                        },
                      ],
                    }}
                    plugins={[pieSlicePercentPlugin]}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => {
                              // dataIndex comes from Chart.js, so nothing here
                              // guarantees it indexes the array this tooltip was
                              // built from.
                              const m = activeReasons[ctx.dataIndex];
                              if (!m) return "";
                              return ` ${m.count.toLocaleString()} · ${fmtPct(m.pct)}`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div className="flex-1 min-w-[220px] flex flex-col gap-2">
                  {reasonMix.map((m) => (
                    <div key={m.key} className="flex items-start gap-2">
                      <span
                        className="mt-1 h-2.5 w-2.5 rounded-sm shrink-0"
                        style={SwatchStyle(m.key)}
                      />
                      <div className="min-w-0">
                        <div className="text-[12px] text-gray-700">
                          <span className="font-medium">{REASON_META[m.key].label}</span>{" "}
                          <span className="tabular-nums text-gray-500">
                            {m.count.toLocaleString()} · {fmtPct(m.pct)}
                          </span>
                        </div>
                        <div className="text-[11px] text-gray-400">{REASON_META[m.key].hint}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <div className="flex flex-col gap-3">
            {worstNoShow && (
              <div className="flex-1 rounded-lg border border-[var(--perf-red)]/40 bg-[color-mix(in_srgb,var(--perf-red)_6%,white)] px-4 py-3">
                <div
                  className="text-[13px] font-semibold"
                  style={{ color: "var(--perf-red-dark)" }}
                >
                  {worstNoShow.label} leads no-shows at {fmtPct(worstNoShow.noShowRate)}
                </div>
                <div className="text-[12px] text-gray-500 mt-0.5">
                  {worstNoShow.noShows} no-shows on {worstNoShow.booked} booked visits — worth a
                  conversation about reminder cadence or a deposit policy.
                </div>
              </div>
            )}
            {recoverable > 0 && (
              <div className="flex-1 rounded-lg border border-[var(--brand-orange-mid)]/40 bg-[color-mix(in_srgb,var(--brand-orange-mid)_6%,white)] px-4 py-3">
                <div
                  className="text-[13px] font-semibold"
                  style={{ color: "var(--brand-orange-dark)" }}
                >
                  {recoverable.toLocaleString()} cancellations are pending reschedule
                </div>
                <div className="text-[12px] text-gray-500 mt-0.5">
                  These are recoverable visits — a follow-up call list is the fastest win on this
                  page.
                </div>
              </div>
            )}
            {!worstNoShow && recoverable === 0 && (
              <div className="flex-1 rounded-lg border border-black/10 bg-white px-4 py-3 grid place-items-center">
                <span className="text-[12px] text-gray-400">
                  No callouts for the selected scope and period.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancellation hotspots */}
      <div>
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-500">
            Cancellation Hotspots
          </h2>
          <ToggleGroup
            type="single"
            variant="outline"
            value={drillLevel}
            onValueChange={(v) => {
              if (v) setDrillLevel(v as DrillLevel);
            }}
            className="gap-0"
          >
            {DRILL_LEVELS.map((l) => (
              <ToggleGroupItem key={l.key} value={l.key} className={TOGGLE_ITEM_CLS}>
                {l.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <p className="text-[12px] text-gray-400 mb-3">
          Ranked by cancellation rate, worst first · Click a column header to re-sort
        </p>

        <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="w-[44px] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.5px] text-gray-400">
                  #
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.5px] text-gray-400">
                  {entityLabel}
                </th>
                <SortableHead label="Booked" sortKey="booked" sort={sort} onToggle={toggleSort} />
                <SortableHead
                  label="Canceled"
                  sortKey="canceled"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHead
                  label="Cancel Rate"
                  sortKey="cancelRate"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHead
                  label="No-Shows"
                  sortKey="noShows"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <SortableHead
                  label="No-Show Rate"
                  sortKey="noShowRate"
                  sort={sort}
                  onToggle={toggleSort}
                />
                <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.5px] text-gray-400 whitespace-nowrap">
                  Top Reason
                </th>
                <SortableHead label="vs. PY" sortKey="vsPy" sort={sort} onToggle={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-[13px] text-gray-400">
                    No data for selected scope and period.
                  </td>
                </tr>
              ) : (
                sorted.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-2.5 text-gray-400 tabular-nums">
                      #{rankById.get(row.id)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="text-gray-700 font-medium">{row.label}</div>
                      {row.subLabel && (
                        <div className="text-[11px] text-gray-400">{row.subLabel}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">
                      {row.booked.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">
                      {row.canceled.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums text-gray-700">
                      {fmtPct(row.cancelRate)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">
                      {row.noShows.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">
                      {fmtPct(row.noShowRate)}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {row.topReason ? (
                        <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-600">
                          <span className="h-2 w-2 rounded-sm" style={SwatchStyle(row.topReason)} />
                          {REASON_META[row.topReason].label}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <RateDeltaVsPY cur={row.cancelRate} py={row.pyCancelRate} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
